import { useId, useState, type FormEvent, type ReactElement } from 'react';
import {
  VOYAGE_MODELS,
  VoyageAppConfigPutBody,
  VoyageAppConfigValidationError,
  voyageModelLabel,
  useUpdateVoyageAppConfig,
  type VoyageAppConfigPutResponse,
  type VoyageModel,
} from '@/lib/voyage';

/**
 * Formulario para configurar la API key de Voyage AI del profile (M2.3).
 *
 * Patrón calcado de `AnthropicAppConfigForm`:
 *
 *  - Modo "create" (sin config previa): instrucciones abiertas, sin precarga.
 *  - Modo "edit"   (con config previa): precargamos `modelDefault` y avisamos
 *    de que reintroducir la apiKey es obligatorio (el backend nunca la
 *    devuelve por GET). En Voyage no llevamos contador de tokens, así que el
 *    aviso es más suave que en Anthropic — no se "pierde" nada al re-guardar.
 */
export function VoyageConfigForm({
  initialModelDefault,
  mode = 'create',
  onSaved,
  onCancel,
}: {
  initialModelDefault?: VoyageModel;
  mode?: 'create' | 'edit';
  onSaved?: (response: VoyageAppConfigPutResponse) => void;
  onCancel?: () => void;
}): ReactElement {
  const apiKeyId = useId();
  const modelId = useId();
  const instructionsId = useId();
  const formErrorsId = useId();

  const [apiKey, setApiKey] = useState('');
  const [modelDefault, setModelDefault] = useState<VoyageModel>(
    initialModelDefault ?? 'voyage-3',
  );
  const [showKey, setShowKey] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(mode === 'create');
  const [clientFieldErrors, setClientFieldErrors] = useState<{
    apiKey?: string;
    modelDefault?: string;
  }>({});

  const mutation = useUpdateVoyageAppConfig();

  const serverValidation =
    mutation.error instanceof VoyageAppConfigValidationError
      ? mutation.error
      : null;
  const otherError =
    mutation.error && !serverValidation ? mutation.error : null;

  function fieldError(field: 'apiKey' | 'modelDefault'): string | undefined {
    if (clientFieldErrors[field]) return clientFieldErrors[field];
    return serverValidation?.fieldErrors[field]?.[0];
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setClientFieldErrors({});

    const parsed = VoyageAppConfigPutBody.safeParse({ apiKey, modelDefault });
    if (!parsed.success) {
      const flat = parsed.error.flatten();
      // `exactOptionalPropertyTypes` rechaza `undefined` para campos opcionales;
      // construimos el objeto sólo con las claves que existen.
      const next: { apiKey?: string; modelDefault?: string } = {};
      const a = flat.fieldErrors.apiKey?.[0];
      const m = flat.fieldErrors.modelDefault?.[0];
      if (a) next.apiKey = a;
      if (m) next.modelDefault = m;
      setClientFieldErrors(next);
      return;
    }

    mutation.mutate(parsed.data, {
      onSuccess: (response) => {
        setApiKey('');
        setShowKey(false);
        if (onSaved) onSaved(response);
      },
    });
  }

  return (
    <section
      aria-labelledby={`${apiKeyId}-title`}
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2
        id={`${apiKeyId}-title`}
        className="text-lg font-semibold text-slate-900"
      >
        {mode === 'edit'
          ? 'Editar configuración de Voyage'
          : 'Configura tu API key de Voyage'}
      </h2>

      <p className="mt-2 text-sm text-slate-700">
        tri-mentor usa Voyage AI para generar los embeddings de tus PDFs y
        permitirte buscar en tu biblioteca personal de triatlón. La key se
        guarda cifrada y nunca se envía al frontend después.
      </p>

      {mode === 'edit' && (
        <p
          role="note"
          className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          Por seguridad la key no se devuelve nunca; debes reintroducirla cada
          vez que edites esta configuración, aunque sólo quieras cambiar el
          modelo.
        </p>
      )}

      {/* Instrucciones colapsables */}
      <div className="mt-4">
        <button
          type="button"
          aria-expanded={instructionsOpen}
          aria-controls={instructionsId}
          onClick={() => setInstructionsOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 rounded"
        >
          <span aria-hidden="true">{instructionsOpen ? '▾' : '▸'}</span>
          {instructionsOpen ? 'Ocultar instrucciones' : 'Cómo conseguir tu API key'}
        </button>

        {instructionsOpen && (
          <ol
            id={instructionsId}
            className="mt-3 list-decimal space-y-2 pl-6 text-sm text-slate-700"
          >
            <li>
              Abre{' '}
              <a
                href="https://www.voyageai.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-700 underline hover:text-teal-800"
              >
                voyageai.com
              </a>{' '}
              y crea una cuenta (o inicia sesión si ya la tienes).
            </li>
            <li>
              En el dashboard ve a <strong>API Keys</strong> y pulsa{' '}
              <strong>Create Key</strong>. Dale un nombre descriptivo
              (p. ej.{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
                tri-mentor
              </code>
              ).
            </li>
            <li>
              Copia la key (empieza por{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
                pa-…
              </code>
              ) y pégala abajo. Voyage no te la mostrará dos veces.
            </li>
            <li>
              Asegúrate de tener crédito activo en tu cuenta. El uso es muy
              barato (~0.02 €/M tokens en voyage-3-lite); subir un libro
              entero cuesta céntimos.
            </li>
          </ol>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
        {/* API Key */}
        <div>
          <label
            htmlFor={apiKeyId}
            className="block text-sm font-medium text-slate-800"
          >
            API Key
          </label>
          <div className="mt-1 flex items-stretch gap-2">
            <input
              id={apiKeyId}
              name="apiKey"
              type={showKey ? 'text' : 'password'}
              autoComplete="off"
              spellCheck={false}
              required
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value.trim())}
              aria-invalid={fieldError('apiKey') ? true : undefined}
              aria-describedby={
                fieldError('apiKey') ? `${apiKeyId}-err` : undefined
              }
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
              placeholder="pa-..."
            />
            <button
              type="button"
              aria-pressed={showKey}
              aria-label={showKey ? 'Ocultar API key' : 'Mostrar API key'}
              onClick={() => setShowKey((v) => !v)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
            >
              {showKey ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          {fieldError('apiKey') && (
            <p
              id={`${apiKeyId}-err`}
              role="alert"
              className="mt-1 text-xs text-rose-700"
            >
              {fieldError('apiKey')}
            </p>
          )}
        </div>

        {/* Modelo por defecto */}
        <div>
          <label
            htmlFor={modelId}
            className="block text-sm font-medium text-slate-800"
          >
            Modelo por defecto
          </label>
          <select
            id={modelId}
            name="modelDefault"
            value={modelDefault}
            onChange={(e) => setModelDefault(e.target.value as VoyageModel)}
            aria-invalid={fieldError('modelDefault') ? true : undefined}
            aria-describedby={
              fieldError('modelDefault') ? `${modelId}-err` : undefined
            }
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
          >
            {VOYAGE_MODELS.map((m) => (
              <option key={m} value={m}>
                {voyageModelLabel(m)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">
            voyage-3 es el equilibrio recomendado. lite es más barato y rápido
            pero pierde algo de precisión en búsquedas; large-2 sólo merece la
            pena si los PDFs son muy técnicos.
          </p>
          {fieldError('modelDefault') && (
            <p
              id={`${modelId}-err`}
              role="alert"
              className="mt-1 text-xs text-rose-700"
            >
              {fieldError('modelDefault')}
            </p>
          )}
        </div>

        {/* Errores generales */}
        {(serverValidation?.formErrors.length || otherError) && (
          <div
            id={formErrorsId}
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
          >
            {serverValidation?.formErrors.length
              ? serverValidation.formErrors.join(' ')
              : otherError?.message ??
                'No se pudo guardar la configuración de Voyage.'}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            aria-busy={mutation.isPending}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={mutation.isPending}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancelar
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
